/**
 * XmlTreeView Component
 * Prikaz XML strukture kao stablo
 */

import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ChevronRight as ChevronIcon,
  Description as FileIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { XmlNode } from '../../../lib/xmlParser';
import { getSampleValue, copyPathToClipboard } from '../utils/helpers';

interface XmlTreeViewProps {
  structure: XmlNode;
  sampleRecords: any[];
  sampleIndex: number;
  recordElement: string;
  expanded: Set<string>;
  onToggleExpand: (path: string) => void;
}

export function XmlTreeView({
  structure,
  sampleRecords,
  sampleIndex,
  recordElement,
  expanded,
  onToggleExpand,
}: XmlTreeViewProps) {
  const renderTreeNode = (node: XmlNode, depth = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expanded.has(node.path);
    const currentValue = !hasChildren
      ? getSampleValue(sampleRecords, sampleIndex, node.path, recordElement)
      : '';

    return (
      <Box key={node.path} sx={{ ml: depth * 2 }}>
        <ListItemButton
          onClick={() => hasChildren && onToggleExpand(node.path)}
          dense
          sx={{ py: 0.5, borderRadius: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            {hasChildren ? (
              isExpanded ? <ExpandIcon fontSize="small" /> : <ChevronIcon fontSize="small" />
            ) : (
              <FileIcon fontSize="small" color="action" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  fontFamily="monospace"
                  fontWeight={hasChildren ? 600 : 400}
                >
                  {node.name}
                </Typography>
                {node.isArray && (
                  <Chip
                    label={`×${node.count}`}
                    size="small"
                    color="info"
                    sx={{ height: 18 }}
                  />
                )}
                {!hasChildren && currentValue && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ maxWidth: 200 }}
                  >
                    = "{currentValue}"
                  </Typography>
                )}
              </Box>
            }
          />
          {!hasChildren && (
            <Tooltip title="Kopiraj path za mapiranje">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  const displayPath = copyPathToClipboard(node.path);
                  toast.info(`Kopirano: ${displayPath}`);
                }}
              >
                <CodeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </ListItemButton>

        {hasChildren && (
          <Collapse in={isExpanded}>
            {node.children!.map(child => renderTreeNode(child, depth + 1))}
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <>
      <Typography variant="subtitle2" gutterBottom>
        XML Struktura
      </Typography>
      <Paper
        variant="outlined"
        sx={{ maxHeight: 500, overflow: 'auto', p: 1 }}
      >
        <List dense disablePadding>
          {renderTreeNode(structure)}
        </List>
      </Paper>
    </>
  );
}
